import clsx from "clsx";
import format from "date-fns/format";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import actions from "@/actions";
import { MasterSelect } from "@/components/common/MasterSelect";
import { GetProgressGraphParams, GetProgressGraphResult } from "@/components/home/ProgressDashboard/actions/types";
import { ProgressGraph } from "@/components/home/ProgressDashboard/components/ProgressGraph";
import { useProgressGraph } from "@/components/home/ProgressDashboard/hooks/useProgressGraph";
import { useDebounceLoading } from "@/hooks/useDebounceLoading";

export const ProgressDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const fetchProgressDashboard = (params: GetProgressGraphParams, callback?: (res: GetProgressGraphResult) => void) => {
    dispatch(actions.progressGraph.getProgressGraph(params, callback));
  };
  const {
    fetching,
    term,
    changeTerm,
    categoryId,
    primaryChargeId,
    areaId,
    deviceId,
    changeCategory,
    changePrimaryCharge,
    changeArea,
    changeDevice,
    categories,
    primaryCharges,
    areas,
    devices,
    updatedAt,
    progressData,
  } = useProgressGraph(fetchProgressDashboard);
  const { loading } = useDebounceLoading(fetching);
  const updateDate = useMemo(() => {
    return updatedAt ? `${t("last_update_date")} ${format(updatedAt, "yyyy-MM-dd HH:mm")}` : "";
  }, [updatedAt]);

  return (
    <>
      <div className={`flex gap-[20px]`}>
        <h2 className="page-ttl">{t("progress_budget_graph")}</h2>
        <div className="flex items-center gap-[5px]">
          <button
            className={clsx("btn h-[20px]", term === "all" ? "btn-light-blue" : "btn-gray")}
            style={{ padding: "0 0.75rem" }}
            onClick={() => changeTerm("all")}
          >
            {t("term_all")}
          </button>
          <button
            className={clsx("btn h-[20px]", term === "two_weeks" ? "btn-light-blue" : "btn-gray")}
            style={{ padding: "0 0.75rem" }}
            onClick={() => changeTerm("two_weeks")}
          >
            {t("term_2weeks")}
          </button>
        </div>
        <div className="flex items-center gap-[5px]">
          <div className="w-160">
            <MasterSelect
              prefix="category"
              options={categories}
              value={categoryId}
              onChange={changeCategory}
              placeholder={t("machines_category")}
              isClearable={true}
            />
          </div>
          <div className="w-160">
            <MasterSelect
              prefix="primary_charge"
              options={primaryCharges}
              value={primaryChargeId}
              onChange={changePrimaryCharge}
              placeholder={t("primary_charge")}
              isClearable={true}
            />
          </div>
          <div className="w-160">
            <MasterSelect
              prefix="area"
              options={areas}
              value={areaId}
              onChange={changeArea}
              placeholder={t("area")}
              isClearable={true}
            />
          </div>
          <div className="w-160">
            <MasterSelect
              prefix="device"
              options={devices}
              value={deviceId}
              onChange={changeDevice}
              placeholder={t("device")}
              isClearable={true}
            />
          </div>
        </div>
        {updatedAt && (
          <div className="grow flex items-end justify-end">
            <span className="py-[5px]">{updateDate}</span>
          </div>
        )}
      </div>
      <div className={`w-full h-[calc(100%-55px)] ${loading ? "loading loading--list" : ""}`}>
        {!progressData ? (
          <></>
        ) : progressData.length === 0 ? (
          // 該当するデータがありません
          <p className="empty-message">{t("no_data")}</p>
        ) : (
          <ProgressGraph progressData={progressData} />
        )}
      </div>
    </>
  );
};
